import { useState } from 'react';
import { Button, Checkbox, Flex } from '@optiaxiom/react';
import { useDecision } from '@optimizely/react-sdk';

import { FormInput, PageHeader } from '@frontend/components';
import { useOpalUser } from '@frontend/hooks';
import { AppPagePadding } from '@frontend/styles/index.ts';
import { ToolsManagementPage } from '@frontend/types';

import { useCreateToolRegistry } from '../../hooks/index.ts';

import { styles } from './AddToolsRegistryForm.css.ts';

type ToolsFormProps = {
  setActivePage: (tab: ToolsManagementPage) => void;
};

export function AddToolsRegistryForm({ setActivePage }: ToolsFormProps) {
  const [formData, setFormData] = useState({
    discovery_url: '',
    name: '',
    bearer_token: '',
  });

  function onSuccess() {
    setFormData({ discovery_url: '', name: '', bearer_token: '' });
    setActivePage('tools-registries');
  }

  const { mutate, isPending } = useCreateToolRegistry(onSuccess);
  const [isSystemToolRegistry, setIsSystemToolRegistry] = useState(false);
  const { opalUser } = useOpalUser();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutate({
      discovery_url: formData.discovery_url,
      name: formData.name,
      owner_email: isSystemToolRegistry ? 'admin@optimizely.com' : opalUser.email,
      instance_id: !isSystemToolRegistry ? opalUser.instance.instance_id : undefined,
      bearer_token: formData.bearer_token || undefined,
    });
  };
  const [decision] = useDecision('manage_system_tools');
  const canAddSystemTools = decision.enabled as boolean;

  return (
    <Flex className={`${styles.formContainer} ${AppPagePadding}`} flexDirection="column">
      <PageHeader title="Add tool registry" />
      <form onSubmit={handleSubmit}>
        <Flex className={styles.formInputsContainer} flexDirection="column">
          <FormInput
            name="name"
            label="Registry Name"
            value={formData.name}
            onChange={handleInputChange}
            placeholder="Example Tools Registry"
            required
          />
          <FormInput
            name="discovery_url"
            label="Discovery URL"
            value={formData.discovery_url}
            onChange={handleInputChange}
            placeholder="https://example.com/discovery"
            required
          />
          <FormInput
            name="bearer_token"
            label="Bearer Token (Optional)"
            value={formData.bearer_token}
            onChange={handleInputChange}
            placeholder="Enter bearer token for service authentication"
            type="password"
          />
          {canAddSystemTools && (
            <Checkbox
              checked={isSystemToolRegistry}
              onChange={() => setIsSystemToolRegistry(!isSystemToolRegistry)}
            >
              System Tool Registry
            </Checkbox>
          )}
        </Flex>
        <Flex className={styles.buttonContainer} justifyContent="flex-end">
          <Button appearance="subtle" onClick={() => setActivePage('tools')}>
            Cancel
          </Button>
          <Button
            type="submit"
            appearance="primary"
            loading={isPending}
            disabled={!formData.name || !formData.discovery_url}
          >
            Save
          </Button>
        </Flex>
      </form>
    </Flex>
  );
}
